import { TZDate } from "@date-fns/tz";

const APP_TIMEZONE = "Europe/Paris";

const padTwo = (value) => String(value).padStart(2, "0");
const getDateValue = (date) =>
  `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(
    date.getDate()
  )}`;

const getTodayDateValue = () => {
  const now = new TZDate(new Date(), APP_TIMEZONE);
  return getDateValue(now);
};

const addDaysToDateValue = (dateValue, days) => {
  const [year, month, day] = String(dateValue)
    .split("-")
    .map((value) => Number(value));
  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    Number.isNaN(days)
  )
    return getTodayDateValue();
  const nextDate = new TZDate(
    year,
    month - 1,
    day + Number(days),
    12,
    0,
    APP_TIMEZONE
  );
  return getDateValue(nextDate);
};

const getDatePartsInTimezone = (date) => {
  const local = new TZDate(date, APP_TIMEZONE);
  if (Number.isNaN(local.getTime())) {
    return { dateValue: getTodayDateValue(), timeValue: "00:00" };
  }
  const dateValue = getDateValue(local);
  const timeValue = `${padTwo(local.getHours())}:${padTwo(local.getMinutes())}`;
  return { dateValue, timeValue };
};

const formatDateValueShort = (dateValue) => {
  const [year, month, day] = String(dateValue || "")
    .split("-")
    .map((value) => Number(value));
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return "";
  }
  return `${padTwo(day)}/${padTwo(month)}`;
};

const getDayColumnsFromStart = (startDateValue, daysCount = 5) => {
  const [year, month, day] = String(startDateValue || "")
    .split("-")
    .map((value) => Number(value));
  const todayDateValue = getTodayDateValue();
  const [fallbackYear, fallbackMonth, fallbackDay] = String(todayDateValue)
    .split("-")
    .map((value) => Number(value));
  const safeYear = Number.isNaN(year) ? fallbackYear : year;
  const safeMonth = Number.isNaN(month) ? fallbackMonth : month;
  const safeDay = Number.isNaN(day) ? fallbackDay : day;
  const safeDaysCount =
    Number.isFinite(Number(daysCount)) && Number(daysCount) > 0
      ? Number(daysCount)
      : 5;
  const baseDate = new TZDate(
    safeYear,
    safeMonth - 1,
    safeDay,
    12,
    0,
    APP_TIMEZONE
  );
  const safeBaseDate = Number.isNaN(baseDate.getTime())
    ? new TZDate(
        fallbackYear,
        fallbackMonth - 1,
        fallbackDay,
        12,
        0,
        APP_TIMEZONE
      )
    : baseDate;

  return Array.from({ length: safeDaysCount }, (_, index) => {
    const date = new TZDate(
      safeBaseDate.getFullYear(),
      safeBaseDate.getMonth(),
      safeBaseDate.getDate() + index,
      12,
      0,
      APP_TIMEZONE
    );
    const safeDate = Number.isNaN(date.getTime())
      ? new TZDate(
          fallbackYear,
          fallbackMonth - 1,
          fallbackDay + index,
          12,
          0,
          APP_TIMEZONE
        )
      : date;

    return {
      dayKey: getDateValue(safeDate),
      weekday: new Intl.DateTimeFormat("fr-FR", {
        timeZone: APP_TIMEZONE,
        weekday: "long",
      }).format(safeDate),
      weekday_short: new Intl.DateTimeFormat("fr-FR", {
        timeZone: APP_TIMEZONE,
        weekday: "short",
      }).format(safeDate),
      dateLabel: new Intl.DateTimeFormat("fr-FR", {
        timeZone: APP_TIMEZONE,
        day: "numeric",
        month: "short",
      }).format(safeDate),
      isToday: getDateValue(safeDate) === todayDateValue,
    };
  });
};

const getDayColumns = () => getDayColumnsFromStart(getTodayDateValue(), 5);

export {
  addDaysToDateValue,
  APP_TIMEZONE,
  formatDateValueShort,
  getDatePartsInTimezone,
  getDayColumns,
  getDayColumnsFromStart,
  getTodayDateValue,
};
